/*
 * SkillUp Africa Community — public website design preview, 19 October 2026.
 * Deliberately buildless: open index.html locally. No application credentials,
 * analytics, submissions, member records or remote dependencies are included.
 * Production route and data integration is described in CODEX_HANDOVER.md.
 */
(() => {
    'use strict';
    const LIVE = '';
    const ORG = 'https://skillupafrica.org';
    const DONATE = 'https://skillupafrica.org/donate/';
    const A = '/visuals/public-20261019/images/';
    const publicURL = function publicURL(value) {
        if (!value.startsWith('#/')) return value;
        const [page, detail] = value.slice(2).split('/');
        return (
            (page ? '/' + (page === 'help' ? 'faqs' : page) : '/') + (detail ? '#' + detail : '')
        );
    };
    const assetUrl = (path) => window.SUAC_ASSETS?.[path] || path;
    const $ = (q, el = document) => el.querySelector(q);
    const $$ = (q, el = document) => [...el.querySelectorAll(q)];
    const esc = (value) =>
        String(value).replace(
            /[&<>"']/g,
            (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
        );
    const paths = {
        arrow: '<path d="M5 19 19 5M5 5h14v14"/>',
        right: '<path d="M4 12h16m-6-6 6 6-6 6"/>',
        down: '<path d="M12 4v16m-6-6 6 6 6-6"/>',
        up: '<path d="M12 20V4m-6 6 6-6 6 6"/>',
        left: '<path d="M20 12H4m6-6-6 6 6 6"/>',
        plus: '<path d="M12 5v14M5 12h14"/>',
        close: '<path d="m6 6 12 12M6 18 18 6"/>',
        menu: '<path d="M4 7h16M4 16h16"/>',
        search: '<circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/>',
        check: '<path d="m5 12 4 4L19 6"/>',
        book: '<path d="M12 6v15M3 4c4-1 7 0 9 2 2-2 5-3 9-2v14c-4-1-7 0-9 2-2-2-5-3-9-2Z"/>',
        people: '<circle cx="9" cy="7" r="3"/><path d="M3 21v-3a6 6 0 0 1 12 0v3m1-17a3 3 0 0 1 0 6m2 4a5 5 0 0 1 3 4v3"/>',
        spark: '<path d="m12 2 2.6 7.4L22 12l-7.4 2.6L12 22l-2.6-7.4L2 12l7.4-2.6Z"/>',
        globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c5 5 5 13 0 18-5-5-5-13 0-18Z"/>',
        heart: '<path d="M12 21 3.5 12.5A5.5 5.5 0 0 1 12 5.6a5.5 5.5 0 0 1 8.5 6.9Z"/>',
        chat: '<path d="M21 11a9 9 0 0 1-9 9 10 10 0 0 1-4-.9L3 21l1.7-5A9 9 0 1 1 21 11Z"/><path d="M8 10h8m-8 4h5"/>',
        briefcase:
            '<rect x="3" y="7" width="18" height="14" rx="2"/><path d="M8 7V4h8v3M3 12c6 4 12 4 18 0m-9 0v4"/>',
        lock: '<rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v2"/>',
        shield: '<path d="m12 2 8 4v6c0 5-8 10-8 10S4 17 4 12V6Z"/><path d="m8 12 3 3 5-6"/>',
        mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 6 9 7 9-7"/>',
        tool: '<path d="m3 21 10-10a6 6 0 0 1 7-8l-4 4 1 3 3 1 4-4a6 6 0 0 1-8 7L6 24" transform="translate(-2 -2)"/>',
        camera: '<path d="M3 7h4l2-3h6l2 3h4v14H3Z"/><circle cx="12" cy="13" r="4"/>',
        play: '<path d="m8 4 12 8-12 8Z"/>',
        pause: '<path d="M8 5v14M16 5v14"/>',
        signal: '<path d="M5 12a10 10 0 0 1 14 0M2 8a14 14 0 0 1 20 0M8 16a6 6 0 0 1 8 0"/><circle cx="12" cy="20" r="1"/>',
        image: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8" cy="8" r="2"/><path d="m3 17 6-5 4 3 4-5 4 4"/>',
        clock: '<circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 3"/>',
        flag: '<path d="M5 22V3c6-4 8 4 15 0v11c-7 4-9-4-15 0"/>',
        leaf: '<path d="M20 3C5 2 1 8 5 15s17 4 15-12Z"/><path d="M3 22 15 9"/>',
        document: '<path d="M14 2H5v20h14V7Zm0 0v6h5M8 12h8m-8 4h8"/>',
    };
    const icon = (name = 'arrow', cls = 'icon') =>
        `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] || paths.arrow}</svg>`;
    const photoDimensions = {
        'beach-community-small.webp': { width: 680, height: 509 },
        'beach-community.webp': { width: 1600, height: 1198 },
        'belonging-small.webp': { width: 510, height: 680 },
        'belonging.webp': { width: 720, height: 960 },
        'camera-learning-small.webp': { width: 680, height: 510 },
        'camera-learning.webp': { width: 1400, height: 1050 },
        'camera-practice-small.webp': { width: 487, height: 650 },
        'camera-practice.webp': { width: 1050, height: 1400 },
        'classroom-small.webp': { width: 680, height: 510 },
        'classroom.webp': { width: 1200, height: 900 },
        'community-group-small.webp': { width: 680, height: 510 },
        'community-group.webp': { width: 1400, height: 1050 },
        'community-wide-small.webp': { width: 680, height: 510 },
        'community-wide.webp': { width: 1800, height: 1350 },
        'confidence-small.webp': { width: 680, height: 680 },
        'confidence.webp': { width: 960, height: 960 },
        'conversation-small.webp': { width: 680, height: 451 },
        'conversation.webp': { width: 1200, height: 795 },
        'digital-workshop-small.webp': { width: 680, height: 510 },
        'digital-workshop.webp': { width: 1400, height: 1050 },
        'facilitator-small.webp': { width: 680, height: 450 },
        'facilitator.webp': { width: 1024, height: 678 },
        'finding-your-voice-small.webp': { width: 650, height: 430 },
        'finding-your-voice.webp': { width: 1500, height: 993 },
        'garage-electronics-small.webp': { width: 650, height: 488 },
        'garage-electronics.webp': { width: 1500, height: 1125 },
        'garage-small.webp': { width: 680, height: 421 },
        'garage.webp': { width: 1600, height: 990 },
        'hands-on-digital-small.webp': { width: 650, height: 488 },
        'hands-on-digital.webp': { width: 1500, height: 1125 },
        'listening-small.webp': { width: 650, height: 650 },
        'listening.webp': { width: 1500, height: 1500 },
        'mentoring-session-small.webp': { width: 650, height: 488 },
        'mentoring-session.webp': { width: 1500, height: 1125 },
        'new-connections-small.webp': { width: 650, height: 430 },
        'new-connections.webp': { width: 1500, height: 993 },
        'peer-learning-small.webp': { width: 650, height: 488 },
        'peer-learning.webp': { width: 1500, height: 1125 },
        'shared-moments-small.webp': { width: 680, height: 451 },
        'shared-moments.webp': { width: 1200, height: 795 },
        'sharing-small.webp': { width: 680, height: 451 },
        'sharing.webp': { width: 1100, height: 729 },
        'sua-community-small.webp': { width: 680, height: 509 },
        'sua-community.webp': { width: 1800, height: 1347 },
        'together-small.webp': { width: 680, height: 450 },
        'together.webp': { width: 1800, height: 1192 },
        'workshop-exchange-small.webp': { width: 650, height: 488 },
        'workshop-exchange.webp': { width: 1500, height: 1125 },
        'workshop-small.webp': { width: 680, height: 509 },
        'workshop.webp': { width: 1600, height: 1198 },
    };
    const photo = (file, alt, { small = false, eager = false, cls = '', position = '' } = {}) => {
        const size = photoDimensions[file + (small ? '-small' : '') + '.webp'];
        const large = photoDimensions[file + '.webp'],
            compact = photoDimensions[file + '-small.webp'];
        return `<img src="${assetUrl(A + file + (small ? '-small' : '') + '.webp')}" alt="${esc(alt)}" width="${size.width}" height="${size.height}" ${compact ? `srcset="${A + file}-small.webp ${compact.width}w, ${A + file}.webp ${large.width}w" sizes="(min-width:900px) 50vw, 100vw"` : ''} loading="${eager ? 'eager' : 'lazy'}" decoding="async"${cls ? ` class="${cls}"` : ''}${position ? ` style="object-position:${position}"` : ''}>`;
    };
    const routeLink = (label, route, kind = 'text-link', ico = 'arrow') =>
        `<a class="${kind}" href="#/${route}">${label}${icon(ico)}</a>`;
    const liveLink = (label, path, kind = 'btn btn-blue', ico = 'arrow') =>
        `<a class="${kind}" href="${LIVE}${path}">${label}${icon(ico)}</a>`;
    const donateLink = (label = 'Donate', kind = 'text-link') =>
        `<a class="${kind}" href="${DONATE}" target="_blank" rel="noopener noreferrer" aria-label="${esc(label)} (opens in a new tab)" title="Opens our donation page in a new tab">${label}${icon('arrow')}</a>`;
    const eye = (text, extra = '') => `<p class="eyebrow ${extra}">${text}</p>`;
    const cap = (text) => `<figcaption class="photo-caption">${text}</figcaption>`;
    const navItems = [
        ['about', 'Discover SUAC'],
        ['learn', 'Learn'],
        ['community', 'Community'],
        ['programmes', 'Programmes'],
        ['stories', 'Stories'],
    ];
    let route = document.body.dataset.page || 'home',
        menuOpen = false,
        observer,
        galleryIndex = 0,
        toastTimer,
        lastRoute = '';
    let motionPaused = false;
    try {
        motionPaused = localStorage.getItem('suac-preview-motion') === 'off';
    } catch (_) {}
    document.documentElement.classList.toggle('motion-off', motionPaused);
    const scrollBehavior = () =>
        motionPaused || matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth';

    const FAQS = [
        [
            'What is SkillUp Africa Community?',
            'SkillUp Africa Community (SUAC) is a lasting home for learning, connection and growth. Explore practical learning through the Academy, take part in member conversations, and bring your development into a private Talent Passport. Whether you are starting out or returning with experience to share, you can find your next step here.',
        ],
        [
            'Is it free to join?',
            'Yes. Community membership is free. Individual courses and programmes may have their own access requirements or costs. Those details are shown before you enrol or apply.',
        ],
        [
            'Do I need to have studied with SkillUp Africa before?',
            'No. You can join when you are discovering a skill, changing direction or looking for a community to grow with. Independent membership is for adults. Age-appropriate access and safeguarding apply to younger people; the joining process explains eligibility.',
        ],
        [
            'How do I begin learning?',
            'Create your account, verify your email and complete your member setup. Then explore the Academy and choose an available course. Your learning stays connected to your membership so you can return and continue.',
        ],
        [
            'I already have a SkillUp Africa account. What should I do?',
            `If you have a Community account, <a href="${LIVE}/sign-in" target="_blank" rel="noopener noreferrer">sign in</a> with your existing email and use account recovery if needed. If you studied on an older SkillUp Africa learning platform, <a href="${LIVE}/help" target="_blank" rel="noopener noreferrer">contact support</a> for guidance before creating another account. Your earlier learning records are handled separately.`,
        ],
        [
            'Is my Talent Passport public?',
            'Where enabled in your account, your Talent Passport is a private space for you. Check your member home for availability. Use it to bring your learning, skills and work into a clearer picture of your development. Public sharing and recruiter access are separate future capabilities.',
        ],
        [
            'Does a course certificate guarantee a job?',
            'No. Each course explains its learning requirements and any completion certificate. A completion certificate records completion of that course; it does not automatically certify professional competency or guarantee employment.',
        ],
        [
            'Can I contribute, volunteer or work with SkillUp Africa?',
            `Yes. You can <a href="#/get-involved">explore ways to contribute</a> and speak with the team about teaching, volunteering or partnership opportunities. Arrangements are agreed with the team; membership is not an automatic placement in a programme or role.`,
        ],
        [
            'How do you help keep the community respectful?',
            `Members follow the <a href="${LIVE}/community-guidelines" target="_blank" rel="noopener noreferrer">Community Guidelines</a>. Reporting and safeguarding routes help people raise concerns, and access controls protect personal information. Visit <a href="${LIVE}/help" target="_blank" rel="noopener noreferrer">Help and contact</a> for support. If someone is in immediate danger, contact local emergency services first.`,
        ],
    ];
    const PROGRAMMES = [
        {
            id: 'academy',
            name: 'SkillUp Africa Academy',
            label: 'Online learning',
            category: 'online',
            image: 'workshop',
            alt: 'Learners taking part in a SkillUp Africa classroom session',
            headline: 'A new skill. A fresh possibility.',
            description:
                'Your learning home inside the Community. Follow courses, return to your lessons and keep building knowledge as your interests and ambitions grow.',
            detail: 'Choose from the learning available in the Academy and follow the access details for each course. A free Community membership gives you a home for your journey; specific learning requirements are explained in the catalogue.',
            topics: ['Practical lessons', 'Learning paths', 'Your own pace'],
            cta: 'Explore the Academy',
            href: '#/learn',
        },
        {
            id: 'garage',
            name: 'SkillUp Africa Garage',
            label: 'Vocational & technical skills',
            category: 'in-person',
            image: 'garage',
            alt: 'SkillUp Africa Garage learners practising garment construction on sewing machines',
            headline: 'Make something. Make progress.',
            description:
                'Practical craft and technical learning, developed through making, testing and trying again. From Creative Fashion to Smart Electronics, ideas become work you can hold.',
            detail: 'Documented Garage work in Ibadan includes garment construction and finishing, practical electronics exercises, and working assemblies. Programme places and current admissions are arranged separately from Community membership.',
            topics: ['Creative Fashion', 'Smart Electronics', 'Learning by doing'],
            cta: 'Read a Garage story',
            href: ORG + '/still-building-what-consistency-looks-like-at-suag-ibadan/',
        },
        {
            id: 'media',
            name: 'SkillUp Africa Media',
            label: 'Creative & media learning',
            category: 'in-person',
            image: 'camera-practice',
            alt: 'A participant operating a video camera on a tripod during a SkillUp Africa workshop',
            headline: 'Find your voice. Develop your craft.',
            description:
                'A home for creative work, storytelling and media learning, connected to SkillUp Africa’s wider radio and television work.',
            detail: 'Creative practice is part of the wider SkillUp Africa story. Discover learning through making, sharing ideas and developing the confidence to communicate. Contact the team for current programme information and participation details.',
            topics: ['Storytelling', 'Creative practice', 'Media'],
            cta: 'Ask about the programme',
            href: '#/get-involved',
        },
        {
            id: 'dsep',
            name: 'Digital Skills Empowerment Programme',
            label: 'DSEP · Digital skills',
            category: 'in-person',
            image: 'facilitator',
            alt: 'A SkillUp Africa facilitator presenting beside a digital display',
            headline: 'Skills for a working future.',
            description:
                'Focused technology learning and personal development, connecting practical study with the habits needed to use a skill with confidence.',
            detail: 'DSEP is part of SkillUp Africa’s established training work. It combines technology learning, applied experience and preparation for work. This page introduces the programme; current cohorts, schedules and admissions are confirmed by the team.',
            topics: ['Digital skills', 'Applied learning', 'Work readiness'],
            cta: 'Discuss DSEP',
            href: '#/get-involved',
        },
        {
            id: 'lumina',
            name: 'Lumina',
            label: 'Learning beyond connectivity',
            category: 'access',
            image: null,
            headline: 'Possibility should travel further than a signal.',
            description:
                'Our distinct learning delivery system for offline and low-connectivity settings, extending learning to communities where reliable internet is limited.',
            detail: 'Lumina belongs to the wider SkillUp Africa ecosystem and retains its own delivery identity. It is designed around offline and low-connectivity learning. Contact the team to discuss access or a potential collaboration.',
            topics: ['Offline learning', 'Low connectivity', 'Learning access'],
            cta: 'Explore a collaboration',
            href: '#/get-involved',
        },
    ];
    const STORIES = [
        {
            id: 'practice',
            title: 'Confidence grows through practice.',
            category: 'Inside the Garage',
            date: '6 February 2026',
            datetime: '2026-02-06',
            image: 'garage',
            alt: 'Learners practising sewing at SkillUp Africa Garage',
            teaser: 'At the Garage, a concept becomes useful when you can put it to work. Progress comes through practice, thoughtful corrections and the decision to keep going.',
            body: 'At SkillUp Africa Garage, learners move between explanation, experimentation and improvement. The experience of working through a task helps a concept become something they can use. This account describes a patient approach: return to the work, make adjustments and build understanding through practice.',
            url: ORG + '/learning-that-happens-through-doing-at-skillup-africa-garage/',
        },
        {
            id: 'discipline',
            title: 'The habits behind lasting progress.',
            category: 'Learning culture',
            date: '6 February 2026',
            datetime: '2026-02-06',
            image: 'peer-learning',
            alt: 'SkillUp Africa participants sharing a laptop during a small-group workshop',
            teaser: 'Showing up, keeping commitments and caring about your work. Small habits make a lasting difference to what learning becomes.',
            body: 'SkillUp Africa reflects on the everyday discipline behind sustainable skill building. Keeping commitments, learning from mistakes, managing time and taking responsibility for the quality of your work all contribute to progress. The story connects these habits with the demands people encounter beyond a training environment.',
            url: ORG + '/the-discipline-behind-sustainable-skill-building/',
        },
        {
            id: 'craft',
            title: 'Different crafts. A shared ambition.',
            category: 'People & practice',
            date: '28 January 2026',
            datetime: '2026-01-28',
            image: 'garage-electronics',
            alt: 'SkillUp Africa Garage learners working together on a scooter during practical electronics training',
            teaser: 'A better garment. A working assembly. Inside the fashion and electronics tracks at SUAG Ibadan, progress takes a practical form.',
            body: 'In this January update from SUAG Ibadan, fashion and electronics learners refine projects through construction, testing and improvement. A finished garment or working assembly becomes a way to practise patience, responsibility and judgement. It is a story about the steady work that follows the excitement of getting started.',
            url: ORG + '/still-building-what-consistency-looks-like-at-suag-ibadan/',
        },
    ];
    const GALLERY = [
        {
            image: 'community-wide',
            alt: 'People wearing blue SkillUp Africa shirts standing together outdoors',
            caption: 'One community. Many journeys.',
            source: 'SkillUp Africa community archive',
        },
        {
            image: 'shared-moments',
            alt: 'SkillUp Africa participants smiling as they gather for a group selfie',
            caption: 'The moments between the lessons matter, too.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'workshop-exchange',
            alt: 'SkillUp Africa participants sharing ideas in a classroom with laptops',
            caption: 'A different perspective can move an idea forward.',
            source: 'SkillUp Africa training archive',
        },
        {
            image: 'digital-workshop',
            alt: 'A SkillUp Africa facilitator addressing learners beside a programme banner',
            caption: 'There is always something more to learn.',
            source: 'SkillUp Africa digital learning archive',
        },
        {
            image: 'finding-your-voice',
            alt: 'A SkillUp Africa participant speaking into a microphone',
            caption: 'Your voice has a place in the conversation.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'new-connections',
            alt: 'Two smiling participants standing together at a SkillUp Africa gathering',
            caption: 'New connections. A shared beginning.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'listening',
            alt: 'A participant listening during a SkillUp Africa session',
            caption: 'Good conversations begin with listening.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'mentoring-session',
            alt: 'A SkillUp Africa facilitator working with a small group around laptops',
            caption: 'A little guidance can open a new way forward.',
            source: 'SkillUp Africa training archive',
        },
        {
            image: 'camera-practice',
            alt: 'A participant operating a video camera on a tripod at a SkillUp Africa workshop',
            caption: 'Getting closer to the craft.',
            source: 'SkillUp Africa media archive',
        },
        {
            image: 'conversation',
            alt: 'Two participants sharing a conversation, one holding a microphone',
            caption: 'Bring a question. Leave with another perspective.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'beach-community',
            alt: 'A SkillUp Africa group smiling and raising their hands at a beach gathering',
            caption: 'Community reaches beyond the classroom.',
            source: 'SkillUp Africa public gallery',
        },
        {
            image: 'confidence',
            alt: 'A smiling SkillUp Africa participant holding a microphone',
            caption: 'The confidence to share what you have to say.',
            source: 'SkillUp Africa soft-skill training archive',
        },
        {
            image: 'garage-electronics',
            alt: 'SkillUp Africa Garage learners working together on a scooter',
            caption: 'A practical challenge. A shared focus.',
            source: 'SkillUp Africa Garage',
        },
        {
            image: 'hands-on-digital',
            alt: 'SkillUp Africa learners practising on laptops during a digital workshop',
            caption: 'Curiosity, put to work.',
            source: 'SkillUp Africa digital learning archive',
        },
        {
            image: 'classroom',
            alt: 'A facilitator presenting to learners during a SkillUp Africa classroom session',
            caption: 'Learning grows through the people around you.',
            source: 'SkillUp Africa training archive',
        },
    ];
    const PILLARS = [
        {
            id: 'learn',
            title: 'Learn',
            headline: 'Put your curiosity<br>to work.',
            body: 'Discover practical learning that helps you do more. Follow lessons, develop a skill and make steady progress through the SkillUp Africa Academy.',
            image: 'hands-on-digital',
            alt: 'SkillUp Africa learners practising on laptops during a digital workshop',
            caption: 'Real practice. New possibilities.',
            link: 'Explore learning',
            route: 'learn',
            icon: 'book',
        },
        {
            id: 'connect',
            title: 'Connect',
            headline: 'Find people who<br>get the journey.',
            body: 'Ask a question. Share an idea. Join conversations with people learning and building alongside you. A useful connection can change the way you see what comes next.',
            image: 'conversation',
            alt: 'Two people sharing a conversation during a SkillUp Africa soft-skill session',
            caption: 'Your people are part of the progress.',
            link: 'Discover the community',
            route: 'community',
            icon: 'people',
        },
        {
            id: 'advance',
            title: 'Advance',
            headline: 'See how far<br>you are growing.',
            body: 'Explore how a private Talent Passport can help you reflect on your development. Availability follows the features enabled in your member account.',
            image: 'confidence',
            alt: 'A smiling person speaking into a microphone at a SkillUp Africa session',
            caption: 'Your progress. Your next step.',
            link: 'Meet your Talent Passport',
            route: 'community/passport',
            icon: 'briefcase',
        },
        {
            id: 'impact',
            title: 'Impact',
            headline: 'Grow. Help someone<br>else grow.',
            body: 'Learning can travel through a family, a workplace and a community. Our wider purpose is to turn personal growth into shared progress, through people who contribute what they know.',
            image: 'beach-community',
            alt: 'The SkillUp Africa community gathered together on a beach',
            caption: 'Bigger possibilities, shared.',
            link: 'Explore our purpose',
            route: 'stories/impact',
            icon: 'heart',
        },
    ];

    const faqList = (indexes) =>
        indexes
            .map(
                (i) =>
                    `<details class="faq-item"><summary>${FAQS[i][0]}</summary><div class="faq-answer">${FAQS[i][1]}</div></details>`,
            )
            .join('');
    const faqSection = (
        indexes = [0, 1, 4, 7],
        title = 'A few things<br>you might be wondering.',
    ) =>
        `<section class="section"><div class="wrap faq-layout"><div class="reveal">${eye('A little clarity', 'blue')}<h2 class="section-title">${title}</h2><p class="muted">A good beginning starts with knowing what to expect.</p>${routeLink('More questions? Start here', 'help')}</div><div class="faq-list reveal reveal-delay">${faqList(indexes)}</div></div></section>`;
    const joinSection = (
        title = 'Bring your ambition.<br>Find your <em>people.</em>',
        text = 'Whatever your next step looks like, you do not have to take it alone.',
    ) =>
        `<section class="join-section"><div class="join-orbit" aria-hidden="true"></div><div class="wrap join-inner"><h2 class="reveal">${title}</h2><div class="reveal reveal-delay"><p>${text}</p>${liveLink('Join the community', '/join', 'btn btn-yellow')}<p class="caption" style="margin:13px 0 0">Free membership. A world of possibility.</p></div></div></section>`;
    const photoCard = (p, extra = '') =>
        `<button class="photo-card ${extra} reveal" type="button" data-programme="${p.id}" aria-label="Discover ${esc(p.name)}"><figure>${photo(p.image, p.alt, { small: true })}<span class="photo-arrow">${icon()}</span></figure><div><div class="card-topline"><span>${p.label}</span></div><h3>${p.name}</h3><p>${p.description}</p></div></button>`;
    const storyCard = (s) =>
        `<article class="photo-card story-card reveal"><a href="#/stories/${s.id}" aria-label="Read ${esc(s.title)}"><figure>${photo(s.image, s.alt, { small: true })}<span class="photo-arrow">${icon()}</span></figure></a><div><div class="card-topline"><span>${s.category}</span><time datetime="${s.datetime}">${s.date}</time></div><h3><a href="#/stories/${s.id}">${s.title}</a></h3><p>${s.teaser}</p>${routeLink('Read the story', 'stories/' + s.id)}</div></article>`;
    const cover = (file, alt, caption, extra = '', position = '') =>
        `<div class="wrap"><figure class="page-cover ${extra}">${photo(file, alt, { eager: true, position })}${cap(caption)}</figure></div>`;
    const pageIntro = (eyebrow, title, body, actions = '', breadcrumb = '') =>
        `<section class="page-intro wrap"><div class="breadcrumb"><a href="#/">Home</a><span>/</span><span>${breadcrumb || eyebrow}</span></div><div class="page-intro-grid"><div>${eye(eyebrow, 'blue')}<h1 class="display">${title}</h1></div><div><p class="lead">${body}</p>${actions ? `<div class="actions">${actions}</div>` : ''}</div></div></section>`;
    const steps = () =>
        `<div class="steps-grid"><article class="step reveal"><div class="step-number">01${icon('plus')}</div><h3>Make yourself<br>at home.</h3><p>Create your free membership, verify your email and tell us a little about your interests.</p></article><article class="step reveal"><div class="step-number">02${icon('plus')}</div><h3>Choose a place<br>to begin.</h3><p>Explore the Academy and Community. Find learning and conversations that speak to your next step.</p></article><article class="step reveal"><div class="step-number">03${icon('plus')}</div><h3>Keep showing up.<br>Keep growing.</h3><p>Learn, ask questions, share what you know and build your own record of progress.</p></article></div>`;
    function pillarPanel(i = 0) {
        const p = PILLARS[i];
        return `<div class="pillar-panel panel-enter" role="tabpanel" id="pillar-panel" aria-labelledby="pillar-${p.id}" tabindex="0"><div class="pillar-copy">${eye('One community. Four ways to grow.', 'blue')}<h3>${p.headline}</h3><p>${p.body}</p>${routeLink(p.link, p.route)}</div><figure class="pillar-picture">${photo(p.image, p.alt, { eager: true })}${cap(p.caption)}</figure></div>`;
    }
    function startResult(i) {
        const data = [
            [
                'A little curiosity is a good place to start. Explore the Academy and find learning you can put to work.',
                'Explore learning',
                'learn',
            ],
            [
                'Find a conversation worth joining. Bring your questions, interests and the things you are figuring out.',
                'Discover the community',
                'community',
            ],
            [
                'Give your development a home. Your private Talent Passport brings your learning, skills and work together.',
                'Explore your Talent Passport',
                'community/passport',
            ],
            [
                'Share a little of what you know. Talk with the team about teaching, volunteering or a partnership.',
                'Find your way to contribute',
                'get-involved',
            ],
        ][i];
        return `<p>${data[0]}</p>${routeLink(data[1], data[2])}`;
    }

    function programmeGrid(filter = 'all') {
        const items = PROGRAMMES.filter((p) => filter === 'all' || p.category === filter);
        return items
            .map((p) =>
                p.id === 'lumina'
                    ? `<article class="programme-wide reveal"><div>${eye('Lumina · Learning access')}<h3>${p.headline}</h3><p>${p.description}</p><button class="text-link" data-programme="lumina" type="button" style="background:none;border-width:0 0 1px;padding-inline:0">Discover Lumina${icon()}</button></div><div class="lumina-art" aria-hidden="true"><div class="lumina-core">${icon('signal')}Lumina</div></div></article>`
                    : photoCard(p),
            )
            .join('');
    }
    function render() {
        closeDialog();
        const detail = location.hash.slice(1);
        if (route === 'programmes' && PROGRAMMES.some((p) => p.id === detail))
            openProgramme(detail);
        else if (route === 'stories' && STORIES.some((s) => s.id === detail)) openStory(detail);
    }
    function initReveals() {
        observer?.disconnect();
        const els = $$('.reveal');
        if (
            !('IntersectionObserver' in window) ||
            matchMedia('(prefers-reduced-motion: reduce)').matches ||
            motionPaused
        )
            return;
        observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                }),
            { threshold: 0.07, rootMargin: '0px 0px -15px 0px' },
        );
        els.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.top < innerHeight && r.bottom > 0) el.classList.add('in-view');
            else {
                el.classList.add('ready');
                observer.observe(el);
            }
        });
    }
    function setMenu(open) {
        menuOpen = open;
        const nav = $('#mobile-menu'),
            btn = $('[data-action="menu"]');
        if (!nav || !btn) return;
        nav.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        btn.innerHTML = icon(open ? 'close' : 'menu');
    }
    function toast(message) {
        clearTimeout(toastTimer);
        const t = $('#toast');
        t.textContent = message;
        t.classList.add('visible');
        toastTimer = setTimeout(() => t.classList.remove('visible'), 3500);
    }
    function setMotion() {
        motionPaused = !motionPaused;
        document.documentElement.classList.toggle('motion-off', motionPaused);
        try {
            localStorage.setItem('suac-preview-motion', motionPaused ? 'off' : 'on');
        } catch (_) {}
        const button = $('[data-action="motion"]');
        if (button) {
            button.setAttribute('aria-pressed', String(motionPaused));
            button.innerHTML =
                icon(motionPaused ? 'play' : 'pause') +
                `<span>${motionPaused ? 'Motion paused · Resume' : 'Pause motion'}</span>`;
        }
        if (motionPaused) {
            observer?.disconnect();
            $$('.reveal').forEach((el) => el.classList.add('in-view'));
        }
        toast(
            motionPaused
                ? 'Motion paused. Explore at your own pace.'
                : 'Motion resumed. Your device’s reduced-motion preference still applies.',
        );
    }
    function openDialog(title, content, cls = '') {
        const previousDialog = $('dialog');
        let returnFocus = previousDialog ? previousDialog._returnFocus : document.activeElement;
        closeDialog();
        if (returnFocus?.closest('#mobile-menu') && $('#mobile-menu')?.hidden)
            returnFocus = $('[data-action="menu"]');
        const d = document.createElement('dialog');
        d.className = cls;
        d.setAttribute('aria-labelledby', 'dialog-title');
        d._returnFocus = returnFocus;
        d.innerHTML = `<div class="dialog-head"><h2 id="dialog-title">${title}</h2><button type="button" class="icon-btn" data-action="close-dialog" aria-label="Close dialog">${icon('close')}</button></div>${content}`;
        $('#dialog-root').append(d);
        normalizeLinks(d);
        document.body.classList.add('dialog-open');
        d.addEventListener('close', () => {
            d.remove();
            if (!$('dialog[open]')) {
                document.body.classList.remove('dialog-open');
                if (returnFocus?.isConnected && !returnFocus.closest('[hidden]'))
                    returnFocus.focus({ preventScroll: true });
            }
        });
        d.addEventListener('click', (e) => {
            if (e.target === d) {
                const r = d.getBoundingClientRect();
                if (
                    e.clientX < r.left ||
                    e.clientX > r.right ||
                    e.clientY < r.top ||
                    e.clientY > r.bottom
                )
                    d.close();
            }
        });
        d.showModal();
        return d;
    }
    function closeDialog() {
        const d = $('dialog');
        if (d) {
            d.close();
            d.remove();
            document.body.classList.remove('dialog-open');
        }
    }
    function openProgramme(id) {
        const p = PROGRAMMES.find((p) => p.id === id);
        if (!p) return;
        const imageMarkup = p.image
            ? photo(p.image, p.alt, { eager: true, cls: 'dialog-image' })
            : '';
        openDialog(
            p.name,
            `${imageMarkup}<div class="dialog-body">${eye(p.label)}<h3>${p.headline}</h3><p>${p.description}</p><p>${p.detail}</p><div class="pill-row">${p.topics.map((t) => `<span class="pill">${t}</span>`).join('')}</div><div class="actions"><a class="btn btn-blue" href="${p.href}"${p.href.startsWith('https') ? ' target="_blank" rel="noopener noreferrer"' : ''}>${p.cta}${icon()}</a>${routeLink('Talk to the team', 'get-involved', 'btn btn-outline')}</div></div>`,
        );
    }
    function openStory(id) {
        const s = STORIES.find((s) => s.id === id);
        if (!s) return;
        openDialog(
            'A perspective from SkillUp Africa',
            `${photo(s.image, s.alt, { eager: true, cls: 'dialog-image' })}<div class="dialog-body">${eye(s.category)}<h3>${s.title}</h3><p class="caption" style="margin-bottom:21px">${s.date}</p><p>${s.body}</p><p class="small">Explore the full programme update on the SkillUp Africa website.</p><div class="actions"><a class="btn btn-blue" href="${s.url}" target="_blank" rel="noopener noreferrer">Read the original story${icon()}</a><button class="btn btn-outline" data-action="close-dialog" type="button">Keep exploring</button></div></div>`,
        );
    }
    function openCourse() {
        openDialog(
            'Screen Printing · Course overview',
            `<div class="dialog-body">${eye('SkillUp Africa Academy')}<h3>A practical skill.<br>A step you can build on.</h3><p>Follow the screen printing process through eight video lessons, covering practical preparation and troubleshooting. Revisit the lessons as you develop your understanding.</p><div class="pill-row"><span class="pill">8 video lessons</span><span class="pill">Practical printing</span></div><p style="margin-top:23px">Follow the course’s stated learning and acknowledgement requirements in the Academy. Any completion certificate records course completion; it is not a professional competency qualification.</p><div class="actions">${liveLink('Open the course catalogue', '/app/learning/courses')}</div></div>`,
        );
    }
    function galleryContent() {
        const g = GALLERY[galleryIndex];
        return `<div class="lightbox-image">${photo(g.image, g.alt, { eager: true })}</div><div class="lightbox-bottom"><div><p>${g.caption}</p><p class="caption">${g.source} · ${galleryIndex + 1} / ${GALLERY.length}</p></div><div class="lightbox-controls"><button type="button" class="icon-btn" data-action="gallery-prev" aria-label="Previous photograph">${icon('left')}</button><button type="button" class="icon-btn" data-action="gallery-next" aria-label="Next photograph">${icon('right')}</button></div></div>`;
    }
    function openGallery(i) {
        galleryIndex = i;
        const d = openDialog(
            'Life in the SkillUp Africa community',
            `<div id="gallery-content">${galleryContent()}</div>`,
            'lightbox',
        );
        d.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                changeGallery(-1);
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                changeGallery(1);
            }
        });
    }
    function changeGallery(delta) {
        galleryIndex = (galleryIndex + delta + GALLERY.length) % GALLERY.length;
        $('#gallery-content').innerHTML = galleryContent();
        $(`[data-action="gallery-${delta > 0 ? 'next' : 'prev'}"]`).focus({ preventScroll: true });
    }

    const SEARCH_INDEX = [
        [
            'Discover SkillUp Africa Community',
            'Our story, purpose, values and roots. A lasting home for learning and growth.',
            'about',
            'Discover SUAC',
        ],
        [
            'Learn with the Academy',
            'Explore practical learning, course access and how to begin.',
            'learn',
            'Learning',
        ],
        [
            'Screen Printing',
            'Eight video lessons in practical preparation and troubleshooting.',
            'learn/featured',
            'Learning',
        ],
        [
            'Find your people',
            'Join useful conversations and grow with the community.',
            'community',
            'Community',
        ],
        [
            'Your private Talent Passport',
            'Bring learning, skills and work into a personal record of progress.',
            'community/passport',
            'Advance',
        ],
        [
            'SkillUp Africa Garage',
            'Vocational and technical learning. Creative Fashion and Smart Electronics.',
            'programmes/garage',
            'Programmes',
        ],
        [
            'SkillUp Africa Media',
            'Creative practice, camera skills, storytelling and media.',
            'programmes/media',
            'Programmes',
        ],
        [
            'Digital Skills Empowerment Programme',
            'DSEP connects technology learning, applied experience and work readiness.',
            'programmes/dsep',
            'Programmes',
        ],
        [
            'Lumina',
            'Offline and low-connectivity learning access.',
            'programmes/lumina',
            'Programmes',
        ],
        [
            'Stories from SkillUp Africa',
            'Perspectives on practice, discipline and real progress.',
            'stories',
            'Stories',
        ],
        [
            'Community photo gallery',
            'Real moments from SkillUp Africa people and programmes.',
            'stories/gallery',
            'Gallery',
        ],
        [
            'Get involved',
            'Share your experience, volunteer, teach or discuss a partnership.',
            'get-involved',
            'Contribute',
        ],
        [
            'Donate to SkillUp Africa',
            'Support the work of SkillUp Africa. Our donation page opens in a new tab.',
            DONATE,
            'Donate',
        ],
        [
            'Help and frequently asked questions',
            'Membership, accounts, sign in, learning, privacy and safeguarding.',
            'help',
            'Support',
        ],
        [
            'Free membership',
            'Understand Community membership and individual programme access.',
            'help',
            'Membership',
        ],
        [
            'Returning to SkillUp Africa',
            'Existing Community accounts, older learning accounts and account recovery.',
            'help',
            'Accounts',
        ],
    ];
    function searchResults(query = '') {
        const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
        const results = SEARCH_INDEX.filter(
            (x) => !terms.length || terms.every((t) => x.join(' ').toLowerCase().includes(t)),
        );
        const node = $('#search-results');
        if (!node) return;
        node.innerHTML = results.length
            ? results
                  .map(
                      ([title, desc, url, type]) =>
                          `<a class="search-result" href="${url.startsWith('https://') ? url : '#/' + url}"${url.startsWith('https://') ? ' target="_blank" rel="noopener noreferrer"' : ''}><p class="caption">${type}</p><h3>${title}${url.startsWith('https://') ? ' ↗' : ''}</h3><p>${desc}</p></a>`,
                  )
                  .join('')
            : `<p class="search-empty">No results for “${esc(query)}”. Try learning, membership, Garage or support.</p>`;
        normalizeLinks(node);
        $('#search-count').textContent = results.length
            ? `${results.length} ${results.length === 1 ? 'result' : 'results'}`
            : 'No results';
    }
    function openSearch() {
        setMenu(false);
        const d = openDialog(
            'Find your way around',
            `<div class="search-input-wrap">${icon('search')}<input id="site-search" type="search" autocomplete="off" maxlength="120" placeholder="Learning, community, programmes…" aria-label="Search this website" autofocus></div><div class="search-results" id="search-results"></div><p id="search-count" class="caption muted" role="status" aria-live="polite" style="padding:0 32px 20px"></p>`,
            'search-dialog',
        );
        searchResults();
        $('#site-search', d).focus();
    }
    function prepareNote(form) {
        if (!form.reportValidity()) return;
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim(),
            topic = String(data.get('interest') || ''),
            message = String(data.get('message') || '').trim();
        if (!name || !message) return;
        const note = `Hello SkillUp Africa team,\n\nMy name is ${name}. I would like to discuss ${topic.toLowerCase()}.\n\n${message}\n\nThank you,\n${name}`;
        const d = openDialog(
            'Your conversation note',
            `<div class="dialog-body">${eye('A useful place to start')}<h3>Your idea, ready<br>to <em>share.</em></h3><p>Review your note, then copy it and use the team’s contact page to start the conversation.</p><div class="mail-preview" id="conversation-note" tabindex="0">${esc(note)}</div><div class="actions"><button class="btn btn-blue" id="copy-note" type="button">Copy my note${icon('document')}</button>${liveLink('Open team contact', '/help', 'btn btn-outline')}</div><p class="small" style="margin-top:20px">Nothing has been sent. This note exists only in your current browser session.</p></div>`,
        );
        $('#copy-note', d).addEventListener('click', async () => {
            try {
                if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
                await navigator.clipboard.writeText(note);
                toast('Your note is copied.');
            } catch (_) {
                const node = $('#conversation-note');
                const range = document.createRange();
                range.selectNodeContents(node);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                toast('Your note is selected. Press Ctrl+C or Command+C to copy.');
            }
        });
    }
    function choosePillar(i, focus = false) {
        const tabs = $$('[data-pillar]');
        tabs.forEach((b, n) => {
            b.setAttribute('aria-selected', String(n === i));
            b.tabIndex = n === i ? 0 : -1;
        });
        $('#pillar-panel-root').innerHTML = pillarPanel(i);
        normalizeLinks($('#pillar-panel-root'));
        if (focus) tabs[i].focus();
    }
    document.addEventListener('click', (e) => {
        const button = e.target.closest('button');
        const link = e.target.closest('a');
        if (link) {
            const destination = new URL(link.href, location.href);
            if (
                destination.pathname === location.pathname &&
                destination.hash &&
                destination.hash === location.hash
            ) {
                const detail = destination.hash.slice(1);
                if (
                    (route === 'stories' && STORIES.some((story) => story.id === detail)) ||
                    (route === 'programmes' &&
                        PROGRAMMES.some((programme) => programme.id === detail))
                ) {
                    e.preventDefault();
                    render();
                }
            }
        }
        if (link?.getAttribute('href')?.startsWith('#/')) {
            const target = link.getAttribute('href');
            closeDialog();
            setMenu(false);
            if (target === location.hash) {
                e.preventDefault();
                const detail = target.split('/')[2];
                if (detail) render();
                else window.scrollTo({ top: 0, behavior: scrollBehavior() });
            }
        }
        if (!button) return;
        if (button.dataset.pillar !== undefined) {
            choosePillar(Number(button.dataset.pillar));
            return;
        }
        if (button.dataset.start !== undefined) {
            $$('[data-start]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
            $('#start-result').innerHTML = startResult(Number(button.dataset.start));
            normalizeLinks($('#start-result'));
            return;
        }
        if (button.dataset.programme) {
            openProgramme(button.dataset.programme);
            return;
        }
        if (button.dataset.gallery !== undefined) {
            openGallery(Number(button.dataset.gallery));
            return;
        }
        if (button.dataset.filter) {
            $$('[data-filter]').forEach((b) =>
                b.setAttribute('aria-pressed', String(b === button)),
            );
            const count = PROGRAMMES.filter(
                (p) => button.dataset.filter === 'all' || p.category === button.dataset.filter,
            ).length;
            $('#programme-grid').innerHTML = programmeGrid(button.dataset.filter);
            $('#filter-count').textContent =
                `${count} ${count === 1 ? 'programme' : 'programmes'} to explore`;
            initReveals();
            return;
        }
        if (button.dataset.interest) {
            $('#contact-interest').value = button.dataset.interest;
            $('#contact').scrollIntoView({ behavior: scrollBehavior() });
            $('#contact-interest').focus({ preventScroll: true });
            return;
        }
        const actions = {
            menu: () => setMenu(!menuOpen),
            search: openSearch,
            motion: setMotion,
            course: openCourse,
            'close-dialog': closeDialog,
            'gallery-next': () => changeGallery(1),
            'gallery-prev': () => changeGallery(-1),
        };
        actions[button.dataset.action]?.();
    });
    document.addEventListener('input', (e) => {
        if (e.target.id === 'site-search') searchResults(e.target.value);
    });
    document.addEventListener('submit', (e) => {
        if (e.target.id === 'contact-form') {
            e.preventDefault();
            prepareNote(e.target);
        }
    });
    document.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            openSearch();
        }
        if (e.key === 'Escape' && menuOpen) {
            setMenu(false);
            $('[data-action="menu"]')?.focus();
        }
        if (
            e.target.matches?.('[data-pillar]') &&
            ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
        ) {
            e.preventDefault();
            let n = Number(e.target.dataset.pillar);
            n =
                e.key === 'Home'
                    ? 0
                    : e.key === 'End'
                      ? 3
                      : (n + (e.key === 'ArrowRight' ? 1 : 3)) % 4;
            choosePillar(n, true);
        }
    });
    let scrollScheduled = false;
    function updateProgress() {
        const y = window.scrollY,
            total = document.documentElement.scrollHeight - innerHeight;
        $('#reading-progress').style.width = `${total > 0 ? Math.min(100, (y / total) * 100) : 0}%`;
        $('#site-header').classList.toggle('scrolled', y > 18);
        const topButton = $('#back-to-top'),
            visible = y > 650;
        topButton.classList.toggle('visible', visible);
        topButton.tabIndex = visible ? 0 : -1;
        topButton.setAttribute('aria-hidden', String(!visible));
        scrollScheduled = false;
    }
    addEventListener(
        'scroll',
        () => {
            if (!scrollScheduled) {
                scrollScheduled = true;
                requestAnimationFrame(updateProgress);
            }
        },
        { passive: true },
    );
    addEventListener(
        'resize',
        () => {
            if (innerWidth > 1120 && menuOpen) setMenu(false);
            updateProgress();
        },
        { passive: true },
    );
    $('#back-to-top').innerHTML = icon('up');
    $('#back-to-top').addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
        $('.brand').focus({ preventScroll: true });
    });
    function normalizeLinks(root) {
        root.querySelectorAll('a[href]').forEach((a) => {
            a.setAttribute('href', publicURL(a.getAttribute('href')));
            if (a.getAttribute('href').startsWith('/')) {
                a.removeAttribute('target');
                a.removeAttribute('rel');
            }
        });
    }
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    preference.addEventListener('change', () => {
        if (preference.matches) {
            observer?.disconnect();
            $$('.reveal').forEach((el) => el.classList.add('in-view'));
        }
    });
    normalizeLinks(document);
    const noteSubmit = $('#contact-form button[type="submit"]');
    if (noteSubmit) noteSubmit.disabled = false;
    const motionButton = $('[data-action="motion"]');
    if (motionButton && motionPaused) {
        motionButton.setAttribute('aria-pressed', 'true');
        motionButton.innerHTML = icon('play') + '<span>Motion paused · Resume</span>';
    }
    initReveals();
    updateProgress();
    addEventListener('hashchange', render);
    render();
})();
